// Dinheiro da conta: soma de produtos, desconto, taxas e total.
//
// Todo o conteúdo deste arquivo estava REPETIDO dentro de
// PointOfSale.components.tsx — as mesmas contas, palavra por palavra, em seis
// lugares (mesa e balcão e delivery, cada um por dois caminhos: digitar o
// número ou clicar na linha da tabela de pedidos). Aqui cada conta existe uma
// vez. Os corpos foram MOVIDOS, não reescritos: o que era variável livre virou
// parâmetro e nada mais.
//
// As coisas que o refactor tinha deixado de pé porque consertar muda o
// valor cobrado do cliente foram DECIDIDAS e CORRIGIDAS no card 25
// (decisão do dono em 15/08/2026 — ver
// `_decisoes-lucco/desconto-da-conta-formato-e-base.md`):
//   1. `taxasDaConta` devolvia `undefined` quando a conta não tinha linha de
//      taxa (orderInfo tipo 7), e `taxaPorTipo`/`totaisDoBalcao` estouravam em
//      `.reduce`. Agora a leitura devolve `[]` e as somas ignoram taxa sem
//      `taxValue`. `reduce` sobre `[]` é 0: nenhum valor que HOJE é calculado
//      com sucesso muda — só a exceção vira 0,00.
//   2. A segunda função de desconto da conta (a que tirava o "%" ANTES de
//      testar se havia "%") nunca entrava no ramo do percentual e lia "10%"
//      como R$ 10,00 fixo. Essa função FOI APAGADA. Existe uma só
//      função de desconto da conta — `descontoDaConta` — e ela honra os dois
//      formatos: percentual ("10%") e valor fixo ("5,00" / "3.00").
//   3. A base do percentual era TRÊS bases diferentes (líquida no balcão,
//      líquida por lugar na mesa, bruta no modal de Contas do Período). Agora é
//      UMA — `baseDoDesconto`, a BRUTA —, a mesma do cupom impresso e do
//      fechamento de caixa. É a única mudança do card que MEXE em valor
//      exibido, e só em conta que tem desconto de produto + desconto
//      percentual de conta ao mesmo tempo.
//
// O formato canônico gravado no banco passa a ser o PERCENTUAL ("10%"): quem
// grava é `AddDiscount.modal.tsx`, e ele parou de congelar o percentual em
// reais no subtotal do instante. O leitor daqui aceita os dois formatos, porque
// o aplicativo e o histórico continuam existindo.
//
// O que continua valendo e NÃO pode mudar: as taxas (couvert incluído) incidem
// sobre `totalDosProdutos` — o preço ORIGINAL, ANTES do desconto da conta.

import {
  LinhaDeInfoDaConta,
  TaxaDaConta,
  TotaisDaConta,
} from "./conta.tipos";

/**
 * Soma dos produtos usando o preço ORIGINAL quando existe. É a base de cálculo
 * das taxas percentuais.
 */
export function totalDosProdutos(products: LinhaDeInfoDaConta[]): number {
  return products.reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    let productPrice;
    if (productInfo.originalProductPrice) {
      productPrice = Number(productInfo.originalProductPrice);
    } else {
      productPrice = Number(productInfo.productPrice);
    }
    return acc + (isNaN(productPrice) ? 0 : productPrice);
  }, 0);
}

/**
 * Soma dos produtos pelo preço praticado (já com o desconto do produto). É a
 * base do subtotal que a tela mostra.
 */
export function totalDosProdutosComDesconto(
  products: LinhaDeInfoDaConta[]
): number {
  return products.reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    let productPrice = Number(productInfo.productPrice);
    return acc + (isNaN(productPrice) ? 0 : productPrice);
  }, 0);
}

/** Soma dos produtos de UM lugar da mesa, pelo preço original. */
export function totalPorPessoa(
  products: LinhaDeInfoDaConta[],
  lugar: number
): number {
  return products.reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    let productPrice;
    if (productInfo.originalProductPrice) {
      productPrice = Number(productInfo.originalProductPrice);
    } else {
      productPrice = Number(productInfo.productPrice);
    }

    // Considera o preço apenas se o produto pertence à pessoa atual
    if (Number(productInfo.peopleNumber) === lugar) {
      return acc + (isNaN(productPrice) ? 0 : productPrice);
    }
    return acc;
  }, 0);
}

/** Soma dos produtos de UM lugar da mesa, pelo preço praticado. */
export function totalPorPessoaComDesconto(
  products: LinhaDeInfoDaConta[],
  lugar: number
): number {
  return products.reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    let productPrice = Number(productInfo.productPrice);

    // Considera o preço apenas se o produto pertence à pessoa atual
    if (Number(productInfo.peopleNumber) === lugar) {
      return acc + (isNaN(productPrice) ? 0 : productPrice);
    }
    return acc;
  }, 0);
}

/** Soma de `totalPorPessoaComDesconto` em todos os lugares da mesa. */
export function totalDaMesaComDesconto(
  products: LinhaDeInfoDaConta[],
  lugares: number
): number {
  let total = 0;
  for (let index = 1; index <= lugares; index++) {
    total += totalPorPessoaComDesconto(products, index);
  }
  return total;
}

/**
 * Lê as taxas gravadas no orderInfo de tipo 7. Vale para mesa, balcão e
 * delivery — é UMA função só.
 *
 * NUNCA devolve `undefined`: conta sem linha tipo 7, ou com a linha mas sem a
 * chave "Order Taxs", devolve `[]`. Era esse o comportamento de
 * `taxasDaContaNaMesa`; o balcão e o delivery usavam uma versão que devolvia
 * `undefined` e derrubava a busca em `.reduce` (bug (c) do card 25).
 * `reduce` sobre `[]` é 0, então nenhum valor que já era calculado com sucesso
 * muda — só a exceção vira 0,00.
 */
export function taxasDaConta(
  orderInfo: LinhaDeInfoDaConta[]
): TaxaDaConta[] {
  const orderInfoTaxs = (orderInfo ?? []).filter(
    (info: LinhaDeInfoDaConta) => info.orderInfoType === 7
  )[0];
  if (!orderInfoTaxs || !orderInfoTaxs.orderInfoValue) {
    return [];
  }
  let parsedOrderTaxs: any;
  try {
    parsedOrderTaxs =
      typeof orderInfoTaxs.orderInfoValue === "string"
        ? JSON.parse(orderInfoTaxs.orderInfoValue)
        : orderInfoTaxs.orderInfoValue;
  } catch (e) {
    console.error("Erro ao fazer parse das taxas da conta:", e);
    return [];
  }
  const taxas = parsedOrderTaxs && parsedOrderTaxs["Order Taxs"];
  return Array.isArray(taxas) ? taxas : [];
}

/**
 * O valor de UMA taxa: percentual sobre `base`, ou valor fixo.
 *
 * Taxa sem `taxValue`, com `taxValue` que não é texto, ou com texto que não é
 * número, vale 0 — nunca `NaN` e nunca uma exceção. É essa guarda que impede
 * o `{"Order Taxs":[{}]}` gravado pelo delivery sem taxa de tipo 5 de fazer o
 * TOTAL da tela sair "NaN".
 */
function valorDaTaxa(tax: TaxaDaConta | undefined, base: number): number {
  if (!tax || !tax.taxValue || typeof tax.taxValue !== "string") {
    return 0;
  }
  if (tax.taxValue.includes("%")) {
    const percentual = parseFloat(
      tax.taxValue.replace("%", "").replace(",", ".")
    );
    return isNaN(percentual) ? 0 : (base * percentual) / 100;
  }
  const valorFixo = parseFloat(tax.taxValue.replace(",", "."));
  return isNaN(valorFixo) ? 0 : valorFixo;
}

/**
 * Uma taxa de um tipo, no balcão e no delivery: percentual sobre o total dos
 * produtos, ou valor fixo.
 *
 * `totalDeProdutos` é o preço ORIGINAL dos produtos, ANTES do desconto da
 * conta — é assim que o couvert (`taxType "2"`) tem que ser calculado, e não
 * pode mudar.
 */
export function taxaPorTipo(
  orderTaxs: TaxaDaConta[],
  totalDeProdutos: number,
  taxType: string
): number {
  return (orderTaxs ?? []).reduce((acc: number, tax: TaxaDaConta) => {
    if (tax && tax.taxType === taxType) {
      return acc + valorDaTaxa(tax, totalDeProdutos);
    }
    return acc;
  }, 0);
}

/**
 * Uma taxa de um tipo na mesa: calculada lugar por lugar, porque uma taxa pode
 * valer só para um lugar (`taxPeople`) e o percentual incide sobre o consumo
 * daquele lugar.
 */
export function taxaPorTipoNaMesa(
  orderTaxs: TaxaDaConta[],
  products: LinhaDeInfoDaConta[],
  lugares: number,
  taxType: string
): number {
  // Inicia a variável que irá armazenar o total de taxas para todas as pessoas
  let totalTaxValue = 0;

  if (!orderTaxs || orderTaxs.length === 0) {
    return totalTaxValue; // Retorna 0 se orderTaxs estiver vazio
  }

  // Itera sobre todas as pessoas na mesa, calculando as taxas individualmente
  for (let index = 1; index <= lugares; index++) {
    const productTotalForPerson = totalPorPessoa(products, index);

    // Calcula a taxa de serviço para a pessoa atual (index).
    // `valorDaTaxa` é a mesma leitura do balcão: taxa sem `taxValue` vale 0 em
    // vez de estourar (card 25).
    totalTaxValue += orderTaxs.reduce((acc: number, tax: TaxaDaConta) => {
      // Verifica se o taxType do objeto corresponde ao taxType esperado
      if (tax && tax.taxType === taxType) {
        // Aplica a taxa se `taxPeople` for indefinido ou corresponder ao índice atual
        if (!tax.taxPeople || tax.taxPeople === String(index)) {
          acc += valorDaTaxa(tax, productTotalForPerson);
        }
      }

      return acc; // Sempre retorna o acumulador atualizado
    }, 0);
  }

  return totalTaxValue;
}

/** Soma de TODAS as taxas da mesa, de qualquer tipo, lugar por lugar. */
export function totalDeTaxasNaMesa(
  orderTaxs: TaxaDaConta[],
  products: LinhaDeInfoDaConta[],
  lugares: number
): number {
  if (!orderTaxs || orderTaxs.length === 0) {
    return 0;
  }

  let totalTaxValue = 0;

  // Itera sobre todas as taxas e pessoas para calcular o valor acumulado de todas as taxas
  for (let index = 1; index <= lugares; index++) {
    const productTotalForPerson = totalPorPessoa(products, index);

    // Mesma leitura tolerante de `valorDaTaxa`: taxa sem `taxValue` vale 0 em
    // vez de estourar (card 25).
    totalTaxValue += orderTaxs.reduce((acc: number, tax: TaxaDaConta) => {
      if (tax) {
        // Verifica se a taxa se aplica à pessoa atual (index)
        if (!tax.taxPeople || tax.taxPeople === String(index)) {
          acc += valorDaTaxa(tax, productTotalForPerson);
        }
      }

      return acc;
    }, 0);
  }

  return totalTaxValue;
}

/**
 * O DESCONTO DA CONTA. É a única função de desconto da conta do sistema — os
 * seis caminhos da tela do PDV (mesa, balcão e delivery, cada um pela busca
 * digitada e pelo clique na tabela de pedidos), o modal de Contas do Período,
 * o modal de Clientes e o rótulo da conta chamam esta aqui.
 *
 * Honra os DOIS formatos que existem no banco, sem nunca confundir um com o
 * outro. O canônico passou a ser o percentual (card 25), mas o valor fixo
 * continua existindo no histórico e no cadastro de convênios:
 *   - percentual, como o aplicativo e agora o web gravam: "10%" com base
 *     30,00 → 3,00;
 *   - valor fixo, como o web gravava antes: "3.00" ou "5,00" → 3,00 / 5,00.
 *
 * `baseDoPercentual` é o subtotal sobre o qual o percentual incide, e tem que
 * vir de `baseDoDesconto` — a base BRUTA, uma só para todos os caminhos. NÃO é
 * a base das taxas: taxa (couvert incluído) incide sobre `totalDosProdutos`, o
 * preço ORIGINAL, e isso não muda.
 *
 * Nada aqui lança: JSON quebrado, `discountValue` ausente, que não seja texto,
 * ou que não seja número ("abc") resultam em 0.
 */
export function descontoDaConta(
  orderInfo: LinhaDeInfoDaConta[],
  baseDoPercentual: number
): number {
  let orderDiscount = 0; // Initialize with 0 to prevent NaN accumulation
  // 1. Localize o objeto que contém a informação de desconto
  const discountInfo = orderInfo?.find((info: LinhaDeInfoDaConta) => info.orderInfoType === 8);

  // 2. Verifique se discountInfo existe e, em seguida, parseie o JSON com segurança
  let orderInfoDiscount: { discountValue?: unknown } = {};
  if (discountInfo && discountInfo.orderInfoValue) {
    try {
      orderInfoDiscount =
        (typeof discountInfo.orderInfoValue === "string"
          ? JSON.parse(discountInfo.orderInfoValue)
          : discountInfo.orderInfoValue) || {};
    } catch (e) {
      console.error("Erro ao fazer parse do desconto:", e);
    }
  }

  // 3. Verificar se existe discountValue antes de prosseguir.
  //    `String(...)` porque o back-end não valida o formato (grava cru o que
  //    o cliente mandar) e um número gravado estouraria no `.includes`.
  if (orderInfoDiscount && orderInfoDiscount.discountValue != null) {
    // Armazenamos a string original
    const originalDiscountValue = String(orderInfoDiscount.discountValue);

    // Se contiver "%", tratamos como porcentagem
    if (originalDiscountValue.includes("%")) {
      // Remover apenas o símbolo e converter vírgula para ponto
      const discountPercentageStr = originalDiscountValue
        .replace("%", "")
        .replace(",", ".");
      const discountPercentage = parseFloat(discountPercentageStr);

      // Verificar se é número válido
      if (!isNaN(discountPercentage)) {
        orderDiscount += (discountPercentage * baseDoPercentual) / 100;
      }
    } else {
      // Caso contrário, tratamos como valor fixo
      const discountAmount = parseFloat(
        originalDiscountValue.replace(",", ".")
      );
      if (!isNaN(discountAmount)) {
        orderDiscount += discountAmount;
      }
    }
  }
  return orderDiscount;
}

/**
 * Desconto de PRODUTO somado da conta inteira (o `productDiscount` gravado em
 * cada linha de produto). NÃO é o desconto da conta — é o outro número, e
 * existe porque o modal de Contas do Período exibe os dois somados.
 */
export function descontoDosProdutos(
  products: LinhaDeInfoDaConta[]
): number {
  return (products ?? []).reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    const productDiscount = Number(productInfo.productDiscount || 0);
    return acc + (isNaN(productDiscount) ? 0 : productDiscount);
  }, 0);
}

/**
 * Soma dos produtos pelo preço BRUTO (`productPrice` + `productDiscount`).
 * É a base que o cupom impresso e o fechamento de caixa usam no back-end
 * (`order.handler.ts`), e a que os modais de Contas do Período já usavam.
 */
export function totalDosProdutosBruto(
  products: LinhaDeInfoDaConta[]
): number {
  return (products ?? []).reduce((acc: number, product: LinhaDeInfoDaConta) => {
    const productInfo = JSON.parse(product.orderInfoValue);
    const productPrice =
      Number(productInfo.productPrice) + Number(productInfo.productDiscount || 0);
    return acc + (isNaN(productPrice) ? 0 : productPrice);
  }, 0);
}

/**
 * A BASE DO DESCONTO PERCENTUAL DA CONTA. Uma só, para o sistema inteiro.
 *
 * Decisão do dono em 15/08/2026 (card 25 — ver
 * `_decisoes-lucco/desconto-da-conta-formato-e-base.md`): a base é a BRUTA,
 * `productPrice + productDiscount`, que é a mesma que o CUPOM IMPRESSO e o
 * FECHAMENTO DE CAIXA usam no back-end (`order.handler.ts:984-990`) e a mesma
 * do aplicativo. Antes disto o PDV web usava a base LÍQUIDA (`productPrice`,
 * já com o desconto do item) e a tela divergia do papel entregue ao cliente
 * sempre que a conta tinha desconto de produto E desconto percentual de conta.
 *
 * Vale para os SEIS caminhos, inclusive a mesa: a mesa usava
 * `totalDaMesaComDesconto`, que ignora produto sem lugar. `totalDaMesaComDesconto`
 * continua sendo o SUBTOTAL EXIBIDO da mesa — deixou de ser a base do desconto.
 *
 * NÃO é a base das taxas: taxa (couvert incluído) incide sobre
 * `totalDosProdutos` — o preço ORIGINAL —, e isso não mudou.
 */
export function baseDoDesconto(products: LinhaDeInfoDaConta[]): number {
  return totalDosProdutosBruto(products);
}

/**
 * Soma de TODAS as taxas da conta, de qualquer tipo, no formato do balcão e do
 * delivery: é o que entra no `total` da tela.
 *
 * Somar todas as taxas sem olhar o tipo é o comportamento que já existia e
 * está mantido de propósito — mexer nisso muda dinheiro fora do card 25. O que
 * mudou é só a guarda: taxa sem `taxValue` vale 0 em vez de virar `NaN`.
 */
export function totalDeTaxas(
  orderTaxs: TaxaDaConta[],
  totalDeProdutos: number
): number {
  return (orderTaxs ?? []).reduce(
    (acc: number, tax: TaxaDaConta) => acc + valorDaTaxa(tax, totalDeProdutos),
    0
  );
}

/** Os totais como a tela da MESA os mostra. */
export function totaisDaMesa({
  products,
  orderTaxs,
  lugares,
  totalComDesconto,
  desconto,
}: {
  products: LinhaDeInfoDaConta[];
  orderTaxs: TaxaDaConta[];
  lugares: number;
  totalComDesconto: number;
  desconto: number;
}): TotaisDaConta {
  const taxa = (taxType: string) =>
    taxaPorTipoNaMesa(orderTaxs, products, lugares, taxType);

  return {
    totalProductsPrice: Number(totalComDesconto).toFixed(2).replace(".", ","),

    totalDiscount: Number(desconto).toFixed(2).replace(".", ","),

    totalProductsPriceWithDiscount: Number(totalComDesconto - desconto)
      .toFixed(2)
      .replace(".", ","),
    subtotal: 5,

    serviceTax: Number(taxa("1")).toFixed(2).replace(".", ","),

    entranceTax: Number(taxa("2")).toFixed(2).replace(".", ","),

    minimumConsumption: Number(taxa("3")).toFixed(2).replace(".", ","),

    parkTax: Number(taxa("4")).toFixed(2).replace(".", ","),

    deliveryTax: Number(taxa("5")).toFixed(2).replace(".", ","),

    customerBalance: "0",

    total: Number(
      totalComDesconto -
        desconto +
        totalDeTaxasNaMesa(orderTaxs, products, lugares) // Soma o total calculado de todas as taxas
    )
      .toFixed(2)
      .replace(".", ","),
  };
}

/**
 * Os totais como a tela do BALCÃO e do DELIVERY os mostram.
 *
 * Diferença que estava no código e continua: as taxas item por item usam
 * `totalDeProdutos` como base do percentual, mas o `total` soma TODAS as taxas
 * de novo — sem olhar o tipo — também sobre `totalDeProdutos`. Mexer nisso
 * muda dinheiro fora do card 25 e foi deixado como estava DE PROPÓSITO.
 *
 * O que mudou no card 25: `orderTaxs` chega sempre como lista (nunca
 * `undefined`) e a soma ignora taxa sem `taxValue` em vez de somar `NaN` —
 * é o que impede o TOTAL de sair "NaN" num delivery sem taxa de entrega.
 */
export function totaisDoBalcao({
  orderTaxs,
  totalDeProdutos,
  totalComDesconto,
  desconto,
}: {
  orderTaxs: TaxaDaConta[];
  totalDeProdutos: number;
  totalComDesconto: number;
  desconto: number;
}): TotaisDaConta {
  const taxa = (taxType: string) =>
    taxaPorTipo(orderTaxs, totalDeProdutos, taxType);

  return {
    totalProductsPrice: Number(totalComDesconto).toFixed(2).replace(".", ","),
    totalDiscount: Number(desconto).toFixed(2).replace(".", ","),
    totalProductsPriceWithDiscount: Number(totalComDesconto - desconto)
      .toFixed(2)
      .replace(".", ","),
    serviceTax: taxa("1").toFixed(2).replace(".", ","),
    entranceTax: taxa("2").toFixed(2).replace(".", ","),
    minimumConsumption: taxa("3").toFixed(2).replace(".", ","),
    parkTax: taxa("4").toFixed(2).replace(".", ","),
    deliveryTax: taxa("5").toFixed(2).replace(".", ","),
    customerBalance: "0",
    total: String(
      (
        totalComDesconto -
        desconto +
        totalDeTaxas(orderTaxs, totalDeProdutos)
      ).toFixed(2)
    ).replace(".", ","),
  };
}
